import React, { memo, useMemo } from 'react';
import classNames from 'classnames';
import ClearIcon from '@mui/icons-material/Clear';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Dialog from '@mui/material/Dialog';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import LinearProgress from '@mui/material/LinearProgress';
import { ModalProps } from './types';

function Modal(props: ModalProps): React.ReactElement {
  /* Global & Local States */
  const {
    open,
    onClose,
    onConfirm,
    typeSize,
    typeTheme = 'custom',
    typeIsLoading,
    disableEscapeKeyDown,
    disableCancelButton,
    disableConfirmButton,
    disableBackdropClick,
    additionAction,
    children,
    sx,
    contentSx,
    actionSx,
    className,
    mainName,
    contentClassName,
    titleClassName,
    actionClassName,
    title,
    closeIcon = true,
    disabledConfirm,
    disabledCancel,
    confirmText,
    cancelText,
    forceFullScreen
  } = props;

  /* Views */
  const renderSize = useMemo(() => {
    switch (typeSize) {
      case 'xs':
        return 'xs';
      case 'sm':
        return 'sm';
      case 'md':
        return 'md';
      case 'lg':
        return 'lg';
      case 'xl':
        return 'xl';
      default:
        return 'sm';
    }
  }, [typeSize]);
  const renderCloseIcon = useMemo<React.ReactNode>(() => {
    if (closeIcon) {
      return (
        <ButtonBase
          sx={{
            position: 'absolute',
            right: '20px',
            top: '20px',
            zIndex: 1000
          }}
          onClick={onClose}
        >
          <ClearIcon sx={{ color: '#000000 !important;' }} fontSize="small" />
        </ButtonBase>
      );
    }
    return <React.Fragment />;
  }, [open, closeIcon, onClose]);
  const renderAPITitle = useMemo<React.ReactNode>(() => {
    if (title) {
      return (
        <Box
          sx={{
            width: '100%',
            height: '40px',
            position: 'relative'
          }}
          className={titleClassName}
        >
          {title}
        </Box>
      );
    }
    return <React.Fragment />;
  }, [title, titleClassName]);
  const renderCustomTitle = useMemo<React.ReactNode>(() => {
    if (title) {
      if (typeof title === 'string') {
        return (
          <Box
            sx={{
              width: '100%',
              height: '40px',
              display: 'flex',
              position: 'relative'
            }}
            className={titleClassName}
          >
            <Typography variant="h2" color="textPrimary" style={{ wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>
              {title}
            </Typography>
          </Box>
        );
      }
      return title;
    }
    return <React.Fragment />;
  }, [title, titleClassName]);
  const renderIsLoading = useMemo<React.ReactNode>(
    () =>
      typeIsLoading && (
        <LinearProgress
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            zIndex: 10000,
            width: '100%'
          }}
        />
      ),
    [typeIsLoading]
  );
  const renderAPICancelButton = useMemo<React.ReactNode>(() => {
    if (!disableCancelButton) {
      return (
        <Button sx={{ mr: 4, minWidth: '96px' }} color="inherit" onClick={onClose} disabled={disabledCancel}>
          {!cancelText ? 'cancel' : cancelText}
        </Button>
      );
    }
    return <React.Fragment />;
  }, [disableCancelButton, disabledCancel, cancelText, onClose]);
  const renderAPIConfirmButton = useMemo<React.ReactNode>(() => {
    if (!disableConfirmButton) {
      return (
        <Button
          sx={{ minWidth: '96px' }}
          color="primary"
          variant="contained"
          onClick={onConfirm}
          disabled={disabledConfirm}
        >
          {!confirmText ? 'confirm' : confirmText}
        </Button>
      );
    }
    return <React.Fragment />;
  }, [disableConfirmButton, disabledConfirm, confirmText, onConfirm]);
  const renderCustomCancelButton = useMemo<React.ReactNode>(() => {
    if (!disableCancelButton) {
      return (
        <Button
          sx={{ mr: disableConfirmButton ? 0 : 4, minWidth: '96px' }}
          variant="text"
          color="inherit"
          onClick={onClose}
          disabled={disabledCancel}
        >
          {!cancelText ? 'cancel' : cancelText}
        </Button>
      );
    }
    return <React.Fragment />;
  }, [disableCancelButton, disableConfirmButton, disabledCancel, cancelText, onClose]);
  const renderCustomConfirmButton = useMemo<React.ReactNode>(() => {
    if (!disableConfirmButton) {
      return (
        <Button
          sx={{ minWidth: '96px' }}
          color="primary"
          variant="contained"
          onClick={onConfirm}
          disabled={disabledConfirm}
        >
          {!confirmText ? 'confirm' : confirmText}
        </Button>
      );
    }
    return <React.Fragment />;
  }, [disableConfirmButton, disabledConfirm, confirmText, onConfirm]);
  const renderContentByTheme = useMemo<React.ReactNode>(() => {
    switch (typeTheme) {
      case 'api':
        return (
          <Box sx={{ px: 6, py: 5, position: 'relative', ...sx }} className={className}>
            {renderCloseIcon}
            {renderAPITitle}
            <Box
              className={contentClassName}
              sx={{ mt: title ? 4 : 0, width: '100%', position: 'relative', ...contentSx }}
            >
              {children}
            </Box>
            <Box
              className={actionClassName}
              sx={{
                pb: forceFullScreen ? 4 : 0,
                mt: disableCancelButton && disableConfirmButton ? 0 : 4,
                width: '100%',
                display: 'flex',
                justifyContent: 'flex-end',
                position: 'relative'
              }}
            >
              {renderAPICancelButton}
              {renderAPIConfirmButton}
            </Box>
            {renderIsLoading}
          </Box>
        );
      case 'custom':
        return (
          <Box sx={{ px: 6, py: 5, position: 'relative', ...sx }} className={className}>
            {renderCloseIcon}
            {renderCustomTitle}
            <Box
              className={contentClassName}
              sx={{ mb: title ? 4 : 0, width: '100%', position: 'relative', ...contentSx }}
            >
              {children}
            </Box>
            <Box
              className={actionClassName}
              sx={{
                pb: forceFullScreen ? 4 : 0,
                mt: disableCancelButton && disableConfirmButton ? 0 : 4,
                width: '100%',
                display: 'flex',
                justifyContent: 'flex-end',
                position: 'relative',
                ...actionSx
              }}
            >
              {additionAction}
              {renderCustomCancelButton}
              {renderCustomConfirmButton}
            </Box>
            {renderIsLoading}
          </Box>
        );
      default:
        return <React.Fragment />;
    }
  }, [
    open,
    onClose,
    onConfirm,
    typeSize,
    typeTheme,
    typeIsLoading,
    disableEscapeKeyDown,
    disableCancelButton,
    disableConfirmButton,
    additionAction,
    children,
    sx,
    contentSx,
    className,
    mainName,
    contentClassName,
    titleClassName,
    actionClassName,
    title,
    closeIcon,
    disabledConfirm,
    forceFullScreen
  ]);

  /* Main */
  return (
    <Dialog
      className={classNames(mainName)}
      open={open}
      onClose={(event, reason) => {
        if (reason !== 'backdropClick' && disableBackdropClick && onClose) {
          onClose();
        }
      }}
      fullScreen={forceFullScreen}
      fullWidth
      maxWidth={renderSize}
      disableEnforceFocus
      disableEscapeKeyDown={disableEscapeKeyDown}
      disablePortal
    >
      {renderContentByTheme}
    </Dialog>
  );
}

export default memo(Modal);
